import { i18n } from "@/services/locale/i18n";

import type { FunnelAnswers } from "../v2/funnel/config";

import type {
  CostCalculatorStorage,
  PageName,
  StepKeyName,
} from "./calculator.types";

const { t } = i18n;

export const CALCULATOR_PAGE_NAME: PageName = "cost_calculator_form";
export const CALCULATOR_STEP_KEY: StepKeyName = "calculator_form";

export const INTAKE_SCHEDULE_PAGE_URL = "/client/intake/schedule-meeting";
export const LSS_SCHEDULE_PAGE_URL = "/client/lss/schedule-meeting";

export const getSignInUrl = (practice: string) =>
  `/${practice}/cost-calculator/auth`;

export const getUnsupportedStateUrl = (practice: string) =>
  `/${practice}/cost-calculator/unsupported-state`;

export const getEstimationUrl = (practice: string) =>
  `/${practice}/cost-calculator/price-estimation`;

export const DEFAULT_COST_ESTIMATION: CostCalculatorStorage["cost"] = {
  min: 2000,
  max: 6000,
  currency: "$",
};

export const COST_CALCULATOR_STORAGE_KEY = "costCalculator";

export const costCalculatorRoutes = {
  path: "/cost-calculator",
} as const;

export const YOUR_CASE_QUESTIONS = {
  LEGAL_PAPERS: "legalPapers",
  HAS_UPCOMING_COURT_EVENT: "hasUpcomingCourtEvent",
  OWN_PROPERTY_TOGETHER: "ownPropertyTogether",
  IS_LOOKING_TO_SETTLE: "isLookingToSettle",
} as const;

// TODO: Implement dynamic questions based on the answers.
// This is a preparation for that.
export const SERVICE_TYPES = {
  SHARED_CUSTODY: {
    value: "I want shared custody with my partner",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_SHARED_CUSTODY"),
  },
  DIVORCE_WITH_CUSTODY: {
    value: "I want a divorce involving child custody",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_DIVORCE_WITH_CUSTODY"),
  },
  CHANGE_CUSTODY_ORDER: {
    value: "I want to change a custody court order",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_CHANGE_CUSTODY_ORDER"),
  },
  SOLE_CUSTODY: {
    value: "I want to file for sole custody",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_SOLE_CUSTODY"),
  },
  OVERDUE_SUPPORT: {
    value: "I need assistance with overdue child support",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_OVERDUE_SUPPORT"),
  },
  FILE_CHILD_SUPPORT: {
    value: "I want to file for child support",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_FILE_CHILD_SUPPORT"),
  },
  CHANGE_SUPPORT_TERMS: {
    value: "I'd like to change my current child support terms",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_CHANGE_SUPPORT_TERMS"),
  },
  INITIATE_DIVORCE: {
    value: "I'm looking to initiate a divorce",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_INITIATE_DIVORCE"),
  },
  RESPOND_DIVORCE: {
    value: "I want to respond to a divorce petition",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_RESPOND_DIVORCE"),
  },
  SOMETHING_ELSE: {
    value: "Something else",
    label: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_SOMETHING_ELSE"),
  },
} as const;

export type ServiceType = (typeof SERVICE_TYPES)[keyof typeof SERVICE_TYPES];

export const SUB_PRACTICE_TO_SERVICE_TYPE_MAP: Record<
  Extract<
    FunnelAnswers["subPractice"],
    "divorce" | "child-custody" | "child-support"
  >,
  ServiceType[]
> = {
  divorce: [
    SERVICE_TYPES.RESPOND_DIVORCE,
    SERVICE_TYPES.INITIATE_DIVORCE,
    SERVICE_TYPES.DIVORCE_WITH_CUSTODY,
    SERVICE_TYPES.CHANGE_CUSTODY_ORDER,
    SERVICE_TYPES.SOLE_CUSTODY,
    SERVICE_TYPES.SOMETHING_ELSE,
  ],
  "child-custody": [
    SERVICE_TYPES.SOLE_CUSTODY,
    SERVICE_TYPES.SHARED_CUSTODY,
    SERVICE_TYPES.CHANGE_CUSTODY_ORDER,
    SERVICE_TYPES.SOMETHING_ELSE,
  ],
  "child-support": [
    SERVICE_TYPES.FILE_CHILD_SUPPORT,
    SERVICE_TYPES.OVERDUE_SUPPORT,
    SERVICE_TYPES.CHANGE_SUPPORT_TERMS,
    SERVICE_TYPES.SOMETHING_ELSE,
  ],
};
